import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import moment from "moment";

import { getFarmDetails, getField, getRedzones } from "services";

import {
  canDeleteField,
  checkExperienceCookieConsent,
  formatNumber,
  getCappedFieldOutcomeBasedPayoutRange,
  getCappedFieldPracticeBasedPayout,
  isAutoEnrolledInventoryProgram,
  isFeatureActiveOnCurrentFarm,
  isFieldEnrolled,
  matchGeoFirstAndLastCoords,
  openCookiePreferences,
  outcomesExistOnField,
  scrollPage,
  useAddField,
  useCancelAsyncOnUnmount,
  useCarbonProgramImpact,
  useCookieConsent,
  useDeleteField,
  useEnrollField,
  useGeneralContext,
  useGetAllCarbonProgramOutcomes,
  useGetAllFieldPracticesByFarm,
  useGetFarmReportingYear,
  useGetFarms,
  useGetFieldShape,
  useGetPracticeCrops,
  useGetVerificationEditingRequests,
  usePracticeManagementCropParameters,
  useSplitFieldDateRestriction,
  useTimelineModal,
  useTranslation,
  useUpdateField,
  useUpdateVerificationEditingRequest,
  useUserContext,
} from "utils";

import { FEATURE_FLAG_SLUGS } from "consts";

import {
  ACCOUNTING_METHODOLOGY,
  AddFieldRequestBody,
  CROP_TYPE_FCPO,
  Coordinates,
  CropFCPO,
  EDITING_REQUEST_TYPE,
  ENROLLMENT_TYPE,
  FarmDetailsResponse,
  FieldDetails as FieldDetailsResponse,
  FieldListField,
  FieldShape,
  PRACTICE_SLUG,
  RedzoneResponse,
  SingleFieldOutcomes,
} from "models";

import {
  Anchor,
  Button,
  CounterBadge,
  Divider,
  Drawer,
  EmptyStateCard,
  ExtraInfo,
  FieldParticipationTags,
  Flex,
  Form,
  Icon,
  IllustrationCard,
  Input,
  KlimRadioGroup,
  LazyLoadImage,
  MapStatic,
  PageSpin,
  PayoutAmount,
  Svg,
  TabLabel,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  Message as message,
} from "components";

import { AddEditCropDrawer } from "pages/app/all-field-view/components/AddEditCropDrawer";
import { CALENDAR_YEAR_START, FARM_YEAR_END, NEW_CROP } from "pages/app/all-field-view/constants/cropForm";
import { useSubmissionStatus } from "pages/app/all-field-view/hooks/useSubmissionStatus";
import {
  getFieldCropsWithMissingTags,
  getFieldMissingDataYears,
  getFieldYearsWithoutMainCrop,
  getMandatoryYearsByPracticeSlug,
} from "pages/app/all-field-view/utils/AllFieldsUtils";
import { EditingRequestBanner } from "pages/app/field-timeline/components/EditingRequestBanner";
import { formatCo2DisplayValue, formatPerHaRange, formatPerHaValue } from "pages/app/field-timeline/utils/PayoutUtils";
import { FieldLocation } from "pages/app/fields/components/FieldLocation";
import { SplitFieldDrawer } from "pages/app/fields/components/SplitFieldDrawer";
import { useSoilSightFieldImport } from "pages/app/scenario-analysis/hooks/useSoilSightFieldImport";

type FieldDetailsProps = {
  fieldId?: string;
  review?: boolean;
  onReviewFinish?: () => void;
  onFinish?: () => void;
  setShowFieldEditDrawer?: (show: boolean) => void;
  setShowFieldSplittingDrawer?: (show: boolean) => void;
  openMapOnMount?: boolean;
};

type FieldFormValues = {
  name: string;
  number?: string;
  isInRedzone?: boolean;
  nearby?: string[];
  editingRequestComment?: { [key: string]: string };
};

export const FieldDetails = ({ fieldId, review: _review, onReviewFinish, onFinish, setShowFieldEditDrawer, openMapOnMount }: FieldDetailsProps) => {
  const { t, ready } = useTranslation(["onboarding", "enums", "fieldTimeline"], { useSuspense: false });
  const { user } = useUserContext();
  const {
    data: { activeFarmReportingYear },
  } = useGetFarmReportingYear(user?.farm?.id || null);
  const { isSubmissionFeatureEnabled, isDataSubmitted, isDataSubmissionReopened } = useSubmissionStatus(activeFarmReportingYear);
  const { currentFarm } = useGetFarms();
  const { data: allFieldsPractices } = useGetAllFieldPracticesByFarm(currentFarm);
  const { data: carbonProgramOutcomes, isLoading: isLoadingCarbonProgramOutcomes } = useGetAllCarbonProgramOutcomes(
    currentFarm?.id,
    currentFarm?.carbonProgram
  );
  const { byField: impactByField, showPayouts: farmHasAllotment } = useCarbonProgramImpact(carbonProgramOutcomes ?? {}, activeFarmReportingYear ?? 0);
  const hasMapCookieConsent = useCookieConsent(checkExperienceCookieConsent);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [field, setField] = useState<FieldDetailsResponse | null>(null);
  const [location, setLocation] = useState<FieldDetailsResponse["location"] | null>(null);
  const [crops, setCrops] = useState<FieldDetailsResponse["crops"]>([]);
  const [nearby, setNearby] = useState<FieldDetailsResponse["nearby"]>([]);
  const [farm, setFarm] = useState<FarmDetailsResponse | null>(null);
  const [showMapDrawer, setShowMapDrawer] = useState(!!openMapOnMount);
  const [locationError, setLocationError] = useState(false);
  const [showPrivacyDrawer, setShowPrivacyDrawer] = useState(false);
  const [showFieldSplitDrawer, setShowFieldSplitDrawer] = useState(false);
  const [fieldShape, setFieldShape] = useState<FieldShape | null>(null);
  const [fieldSize, setFieldSize] = useState<FieldDetailsResponse["size"] | null>(null);
  const [redzone, setRedzone] = useState<RedzoneResponse | null>(null);

  const [showLearnMoreDrawer, setShowLearnMoreDrawer] = useState(false);
  const [showCropEditDrawer, setShowCropEditDrawer] = useState(false);
  const [editingCrop, setEditingCrop] = useState<CropFCPO | null>(null);
  const [activeTab, setActiveTab] = useState<"overview" | "settings">("overview");
  const [selectedMissingDataYear, setSelectedMissingDataYear] = useState<number | null>(null);
  const [isEditingName, setIsEditingName] = useState(false);
  const nameContainerRef = useRef<HTMLDivElement>(null);

  const [form] = Form.useForm<FieldFormValues>();
  const isInRedzone = Form.useWatch("isInRedzone", form);
  const watchedName = Form.useWatch("name", form);

  const { data: practiceCrops } = useGetPracticeCrops({ regionId: field?.regionId });
  const { data: cropParameters } = usePracticeManagementCropParameters();

  const signal = useCancelAsyncOnUnmount()();
  const { fieldShape: fieldShapeResponse, fieldShapeLoading } = useGetFieldShape({ fieldId: fieldId || "", highQuality: true });
  const { mutate: updateField } = useUpdateField({ farmId: user?.farm?.id || "" });
  const { mutate: addField } = useAddField();
  const { mutateAsync: deleteFieldAsync } = useDeleteField();
  const { showModal, hideModal } = useTimelineModal();
  const { setNavigationCrumbs } = useGeneralContext();
  const { data: editingRequests } = useGetVerificationEditingRequests({ farmId: user?.farm?.id || "" });
  const { mutateAsync: submitEditingRequestComment } = useUpdateVerificationEditingRequest({ farmId: user?.farm?.id || "" });

  const hasFieldSplitEditingRequest = editingRequests?.some(
    (request) => request.fieldId === fieldId && request.requestType === EDITING_REQUEST_TYPE.FIELD_SPLIT && !request.completedAt
  );
  const { hasSubmissionForActiveYear, payoutApproved } = useSplitFieldDateRestriction();
  const showFieldSplitButton = hasFieldSplitEditingRequest || payoutApproved || !hasSubmissionForActiveYear;

  const isEnrolled = fieldId ? isFieldEnrolled(allFieldsPractices?.[fieldId]?.availablePracticeBundles ?? [], currentFarm?.carbonProgram ?? null) : false;
  const canDelete = canDeleteField({
    availablePracticeBundles: fieldId ? allFieldsPractices?.[fieldId]?.availablePracticeBundles : undefined,
    carbonProgram: currentFarm?.carbonProgram,
  });

  const fieldHasActivePractices = fieldId ? allFieldsPractices?.[fieldId]?.availablePracticeBundles?.some((bundle) => bundle.active) ?? false : false;
  const hectareLimitWouldBeExceeded = !field || (field.exceedsHectareLimit && !fieldHasActivePractices);
  const isPracticeEnrollmentDisabled =
    currentFarm?.carbonProgram?.fieldsManagement?.fieldEnrollmentType !== ENROLLMENT_TYPE.auto && (hectareLimitWouldBeExceeded || !currentFarm?.carbonProgram);

  const fieldAvailablePracticeBundles = useMemo(
    () => (fieldId ? allFieldsPractices?.[fieldId]?.availablePracticeBundles ?? [] : []),
    [allFieldsPractices, fieldId]
  );
  const requiredPractices = currentFarm?.carbonProgram?.fieldsManagement.practices?.filter((practice) => practice.required) ?? [];
  const hasAnyRequiredPracticesWithConfirmedData = requiredPractices.some(
    (practice) => !!fieldAvailablePracticeBundles.find((bundle) => bundle.slug === practice.slug)?.hasConfirmedData
  );
  const hasAnyDiscontinuedRequiredPractices = requiredPractices.some((practice) =>
    Number.isInteger(fieldAvailablePracticeBundles.find((bundle) => bundle.slug === practice.slug)?.discontinuedYear)
  );
  const mineralFertilizerBundle = fieldAvailablePracticeBundles.find((bundle) => bundle.slug === PRACTICE_SLUG.MINERAL_FERTILIZERS);
  const { handleEnrollField, enrollmentLoading } = useEnrollField({
    fieldId,
    farmId: currentFarm?.id,
    isMineralFertilizerActive: mineralFertilizerBundle?.active,
  });
  const isEnrollmentLockedBySubmission = isDataSubmitted && !isDataSubmissionReopened;
  const isEnrollmentActionDisabled =
    isPracticeEnrollmentDisabled || hasAnyDiscontinuedRequiredPractices || hasAnyRequiredPracticesWithConfirmedData || isEnrollmentLockedBySubmission;

  const getEnrollmentDisabledReason = () => {
    if (hasAnyDiscontinuedRequiredPractices) return t("fieldDetails.carbonProgram.enrollmentDisabledRejected");
    if (hasAnyRequiredPracticesWithConfirmedData) return t("fieldDetails.carbonProgram.expectedPayoutTooltipVerified");
    if (isEnrollmentLockedBySubmission) return t("fieldDetails.carbonProgram.enrollmentDisabledDataSubmitted");
    if (isPracticeEnrollmentDisabled && field?.exceedsHectareLimit) return t("farm:contextMenu.hectareLimitHelperText");
    return undefined;
  };
  const enrollmentDisabledReason = getEnrollmentDisabledReason();
  const isInventoryProgram = currentFarm?.carbonProgram?.accountingMethodology === ACCOUNTING_METHODOLOGY.inventory;
  // TODO: Remove feature flag
  const showCarbonProgramCard =
    isFeatureActiveOnCurrentFarm(FEATURE_FLAG_SLUGS.SOIL_PLUS_3_DATA_GAP) ||
    currentFarm?.carbonProgram?.fieldsManagement?.fieldEnrollmentType === ENROLLMENT_TYPE.user ||
    isInventoryProgram;
  const showEnrollmentActions = !isAutoEnrolledInventoryProgram(currentFarm?.carbonProgram);

  // ── SoilSight promo card ──────────────────────────────────────────────────────
  const { isSoilSightIntegrationActive, canSubmitSoilSightImport, isSoilSightImportSubmitting, handleSoilSightCTAClick } = useSoilSightFieldImport(fieldId);

  const rawFieldOutcomes = fieldId ? (carbonProgramOutcomes?.[fieldId] as SingleFieldOutcomes) : null;
  const fieldOutcomesExist = rawFieldOutcomes?.fieldOutcomes ? outcomesExistOnField(rawFieldOutcomes.fieldOutcomes) : false;
  // Soil+3 field cells show the minimum (guaranteed) payout rather than the average — mirror that
  // choice here so the drawer never disagrees with the field cell for the same field.
  const shouldUseMinimumPayout = isFeatureActiveOnCurrentFarm(FEATURE_FLAG_SLUGS.SOIL_PLUS_3_REWARDS);
  const fieldImpact = fieldId ? impactByField[fieldId] : undefined;
  const fieldPayout = fieldOutcomesExist ? (shouldUseMinimumPayout ? fieldImpact?.payoutMin : fieldImpact?.payoutAvg) ?? null : null;
  // CO2 must come from the same end of the range as the payout, otherwise the two can disagree in sign
  const fieldCo2 = shouldUseMinimumPayout ? fieldImpact?.co2Min : fieldImpact?.co2Avg;

  const payoutDisplayValue = fieldPayout ?? 0;

  const inventoryPayoutRange =
    isInventoryProgram && fieldOutcomesExist && rawFieldOutcomes && activeFarmReportingYear
      ? getCappedFieldOutcomeBasedPayoutRange(rawFieldOutcomes, activeFarmReportingYear)
      : null;

  const showPayout =
    isEnrolled &&
    fieldOutcomesExist &&
    (isInventoryProgram
      ? !!inventoryPayoutRange
      : currentFarm?.carbonProgram?.accountingMethodology === ACCOUNTING_METHODOLOGY.intervention && farmHasAllotment);

  // An inventory field without a payout range has an invalid year, which withholds its outcome-based payout
  const isMissingPayoutData =
    isEnrolled && !isLoadingCarbonProgramOutcomes && (!fieldOutcomesExist || (isInventoryProgram && !!activeFarmReportingYear && !inventoryPayoutRange));

  const fieldManagementFeatures = currentFarm?.carbonProgram?.platformFeatures?.fieldManagement;
  const showOutcomeBasedPayout = !isInventoryProgram || !!fieldManagementFeatures?.outcomeBasedIncentives;
  const showPracticeBasedPayout = isInventoryProgram && !!fieldManagementFeatures?.practiceBasedIncentives;
  // Practice incentives are calculated even when the field's outcomes have errors, so this doesn't depend on fieldOutcomesExist
  const practiceBasedPayout = isEnrolled && rawFieldOutcomes?.fieldOutcomes ? getCappedFieldPracticeBasedPayout(rawFieldOutcomes) : 0;
  // A payout that rounds to zero shows a dash, like in the inventory impact panel
  const hasPracticeBasedPayout = Math.round(practiceBasedPayout) > 0;

  // years for which each practice is mandatory (same logic as the "Add missing data" indicator on the all-fields page)
  const mandatoryYearsByPracticeSlug = useMemo(
    () =>
      currentFarm?.carbonProgram && activeFarmReportingYear
        ? getMandatoryYearsByPracticeSlug(fieldAvailablePracticeBundles, currentFarm.carbonProgram, activeFarmReportingYear)
        : {},
    [fieldAvailablePracticeBundles, currentFarm?.carbonProgram, activeFarmReportingYear]
  );

  const fieldPracticeData = fieldId ? allFieldsPractices?.[fieldId] : null;
  const fieldCrops = useMemo(() => fieldPracticeData?.crops ?? [], [fieldPracticeData]);
  // shared with the Map pin (useFieldStatusMap)
  const cropsWithMissingTags = useMemo(
    () =>
      getFieldCropsWithMissingTags(
        fieldCrops,
        fieldPracticeData?.practices ?? [],
        mandatoryYearsByPracticeSlug,
        currentFarm?.carbonProgram ?? null,
        fieldAvailablePracticeBundles,
        isEnrolled,
        practiceCrops,
        cropParameters
      ),
    [
      fieldCrops,
      fieldAvailablePracticeBundles,
      fieldPracticeData,
      mandatoryYearsByPracticeSlug,
      currentFarm?.carbonProgram,
      isEnrolled,
      practiceCrops,
      cropParameters,
    ]
  );
  const cropsWithMissingTagsByYear = useMemo(() => {
    const result: Record<number, { crop: CropFCPO; tags: string[] }[]> = {};
    for (const item of cropsWithMissingTags) {
      const { year } = item.crop;
      if (year !== undefined) {
        result[year] ??= [];
        result[year].push(item);
      }
    }
    return result;
  }, [cropsWithMissingTags]);

  const yearsWithoutMainCrop = useMemo(
    () => getFieldYearsWithoutMainCrop(fieldCrops, mandatoryYearsByPracticeSlug),
    [mandatoryYearsByPracticeSlug, fieldCrops]
  );

  const allMissingDataYears = useMemo(() => getFieldMissingDataYears(cropsWithMissingTags, yearsWithoutMainCrop), [cropsWithMissingTags, yearsWithoutMainCrop]);

  const activeMissingDataYear =
    selectedMissingDataYear !== null && allMissingDataYears.includes(selectedMissingDataYear) ? selectedMissingDataYear : allMissingDataYears[0] ?? null;

  const handleAddCropForYear = (year: number) => {
    setEditingCrop(NEW_CROP(CROP_TYPE_FCPO.MAIN, moment(`${CALENDAR_YEAR_START}${year}`).toISOString(), moment(`${FARM_YEAR_END}${year}`).toISOString()));
    setShowCropEditDrawer(true);
  };

  const renderMissingCropRows = (items: { crop: CropFCPO; tags: string[] }[]) =>
    items.map(({ crop, tags }) => (
      <div key={crop.id} className="field-details-missing-crop-row">
        <div className="field-details-missing-crop-name-col">
          <span className="field-details-missing-crop-bullet" />
          <Typography variant="paragraph-m" bold className="margin-0">
            {crop.cropName}
          </Typography>
        </div>
        <div className="field-details-missing-crop-tags">
          {tags.map((tag) => (
            <Tag key={tag} colour="grey" text={t(`fieldDetails.missingCrops.${tag}`)} className="field-details-no-payout-badge" />
          ))}
        </div>
        <Button
          type="link"
          className="field-details-missing-crop-fill"
          onClick={() => {
            setEditingCrop(crop);
            setShowCropEditDrawer(true);
          }}
        >
          {t("fieldDetails.missingCrops.fillIn")} →
        </Button>
      </div>
    ));

  const renderMissingCropsYearContent = (year: number) =>
    yearsWithoutMainCrop.includes(year) ? (
      <div className="field-details-missing-crops-empty">
        <Typography variant="paragraph-m" color="grey-600" bold className="margin-0">
          {t("fieldDetails.missingCrops.noCropsAdded.title")}
        </Typography>
        <Typography variant="paragraph-s" color="grey-600" className="margin-0">
          {t("fieldDetails.missingCrops.noCropsAdded.subtitle")}
        </Typography>
        <Button type="link" className="field-details-missing-crop-fill" onClick={() => handleAddCropForYear(year)}>
          {t("fieldDetails.missingCrops.noCropsAdded.addCrop")}
        </Button>
      </div>
    ) : (
      renderMissingCropRows(cropsWithMissingTagsByYear[year] ?? [])
    );

  const fieldListField = useMemo(
    (): FieldListField | null =>
      field && fieldId
        ? { id: fieldId, isInRedzone: field.isInRedzone, location: field.location, name: field.name, redzones: [], regionId: field.regionId, size: field.size }
        : null,
    [field, fieldId]
  );

  const expectedPayoutTooltip = isMissingPayoutData
    ? t("fieldDetails.carbonProgram.expectedPayoutTooltipMissingData")
    : t("fieldDetails.carbonProgram.expectedPayoutTooltipNoPayout");

  const loadField = useCallback(() => {
    if (!fieldId) {
      setLoading(false);
      return;
    }

    getField({ id: fieldId || "", signal })
      .then((res) => {
        setField(res);
        setFieldSize(res.size);
        setCrops(res.crops);
        setNearby(res.nearby);
        setLocation(res.location || location);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [fieldId, location, signal]);

  const loadFarmDetails = useCallback(() => {
    getFarmDetails({ farmId: user?.farm?.id || "" }).then((res) => {
      setFarm(res);
    });
  }, [user?.farm?.id]);

  const loadRedzone = useCallback(() => {
    if (!fieldShape || !farm) return;
    getRedzones({ ...fieldShape.center, includeGeometry: true, signal }).then((res) => {
      setRedzone(res[0] || null);
    });
  }, [fieldShape, farm, signal]);

  const submitEditingRequestComments = async (formValues: FieldFormValues) => {
    const comments = formValues?.editingRequestComment || {};
    const commentSubmissions = Object.entries(comments)
      .filter(([_, comment]) => comment && typeof comment === "string" && comment.trim())
      .map(async ([type, comment]) => {
        const request = editingRequests?.find((r) => r.fieldId === fieldId && r.requestType === type && r.completedAt === null);
        if (request) {
          await submitEditingRequestComment({ requestId: request.id, userComment: comment });
        }
      });
    if (commentSubmissions.length > 0) {
      await Promise.allSettled(commentSubmissions);
    }
  };

  const complete = async (v: FieldFormValues) => {
    const values = { ...v };
    if (!values.name) {
      message({ content: t("errors.mandatoryFields"), icon: "error" });
      return;
    }
    if (!fieldShape || !location || !fieldSize) {
      message({ content: t("fieldDetails.errors.shape"), icon: "error" });
      return setLocationError(true);
    }

    const assignedRedzone: AddFieldRequestBody["redzone"] = redzone
      ? {
          id: redzone.id,
          state: redzone.state,
          accurate: redzone.accurate,
          confirmed: !!isInRedzone,
          geometry: { center: fieldShape.center, geometry: redzone.geometry, type: fieldShape.type },
        }
      : null;

    if (fieldId) {
      setSaving(true);
      updateField(
        {
          ...values,
          ...{
            location,
            crops,
            id: fieldId,
            nearby,
            geometry: { center: fieldShape.center, geometry: fieldShape.geometry, type: fieldShape.type },
            size: fieldSize,
            redzone: assignedRedzone,
            deleteRedzone: !assignedRedzone,
            signal,
          },
        },
        {
          onSuccess: async () => {
            await submitEditingRequestComments(values);
            setNavigationCrumbs((prev) => ({
              ...prev,
              crumbs: prev.crumbs.map((crumb, index) => (index === 3 ? { ...crumb, name: values.name } : crumb)),
            }));
            message({ content: t("fieldSaved"), icon: "tick" });
            if (onReviewFinish) {
              onReviewFinish();
            } else {
              setShowFieldEditDrawer?.(false);
            }
          },
          onSettled: () => {
            setSaving(false);
          },
        }
      );
    } else {
      setSaving(true);
      addField(
        {
          name: values.name,
          location,
          crops,
          nearby,
          geometry: { center: fieldShape.center, geometry: fieldShape.geometry, type: fieldShape.type },
          size: fieldSize,
          redzone: assignedRedzone,
          signal,
        },
        {
          onSuccess: () => {
            message({ content: t("fieldCreated"), icon: "tick" });
            if (onFinish) {
              onFinish();
              return;
            }
            if (setShowFieldEditDrawer) {
              setShowFieldEditDrawer(false);
              return;
            }
            setLocation(null);
            setFieldShape(null);
            scrollPage();
          },
          onSettled: () => {
            setLoading(false);
            setSaving(false);
          },
        }
      );
    }
  };

  const incomplete = (errorInfo: { values: FieldFormValues }) => {
    if (!errorInfo.values.name) {
      message({ content: t("fieldDetails.errors.name"), icon: "error" });
      if (!fieldShape) setLocationError(true);
      return;
    }
    if (!fieldShape && (!location || !(location.lat && location.lng))) {
      message({ content: t("fieldDetails.errors.shape"), icon: "error" });
      return setLocationError(true);
    }
  };

  const setCurrentLocation = (location: Coordinates | null) => {
    if (!location) return;
    setLocationError(false);
    setLocation({ lat: location.lat, lng: location.lng });
  };

  useEffect(() => {
    if (isEditingName) {
      nameContainerRef.current?.querySelector("input")?.focus();
    }
  }, [isEditingName]);

  // If the field has no name after loading, enter edit mode but don't validate until user interacts
  useEffect(() => {
    if (!loading && !fieldShapeLoading && !field?.name) {
      setIsEditingName(true);
    }
  }, [loading, fieldShapeLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleMapOpen = () => setShowMapDrawer(true);
  const handlePrivacyDrawer = () => setShowPrivacyDrawer(!showPrivacyDrawer);
  const showLocationMessage = () => message({ content: t("fieldDetails.fieldSizeMessage"), icon: "tick" });

  useEffect(() => {
    if (fieldShapeResponse) {
      setFieldShape({ ...fieldShapeResponse, geometry: matchGeoFirstAndLastCoords(fieldShapeResponse.geometry) });
      setLoading(false);
    }
  }, [fieldShapeResponse]);

  useEffect(() => {
    setLoading(true);
    if (fieldId) {
      loadField();
    } else {
      setLoading(false);
    }
  }, [farm, fieldId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (fieldShapeLoading) return;
    if (fieldId && !fieldShape) {
      setLocationError(true);
    } else if (fieldShape) {
      setLocationError(false);
    }
  }, [fieldShape, fieldId, fieldShapeLoading]);

  useEffect(() => {
    loadFarmDetails();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadRedzone();
  }, [loadRedzone]);

  const onModalSubmit = useCallback(
    async (fieldId: string) => {
      await deleteFieldAsync({ fieldId });
      hideModal();
      setShowFieldEditDrawer?.(false);
      onFinish?.();
    },
    [deleteFieldAsync, hideModal, setShowFieldEditDrawer, onFinish]
  );

  const handleDeleteConfirmation = useCallback(() => {
    if (field) {
      showModal({
        title: t("farm:deleteModalConfirm.fieldTitle", { fieldName: field.name }),
        message: t("farm:deleteModalConfirm.fieldMessage", { fieldName: field.name }),
        confirmText: t("farm:deleteModalConfirm.deleteText"),
        cancelText: t("farm:deleteModalConfirm.cancel"),
        onConfirm: () => onModalSubmit(field.id),
      });
    }
  }, [field, onModalSubmit, t, showModal]);

  const displayedFieldSize = fieldSize ?? field?.size ?? 0;

  const metaParts = [
    displayedFieldSize ? `${formatNumber({ number: displayedFieldSize, decimalPlaces: 2 })} ha` : null,
    isInRedzone ?? redzone?.accurate ? t("fieldDetails.redzone") : null,
    field?.number ? `Nr. ${field.number}` : null,
  ].filter(Boolean);

  return ready ? (
    <div className="field-details">
      {/* Privacy Drawer */}
      {showPrivacyDrawer && (
        <Drawer title={t("privacy.headline")} visible={showPrivacyDrawer} onClose={handlePrivacyDrawer}>
          <IllustrationCard>
            <Svg svgName="misc/data_privacy" />
          </IllustrationCard>
          <Typography variant="h2" className="margin-top-200 margin-bottom-200">
            {t("privacy.title")}
          </Typography>
          <Typography variant="paragraph-m">{t("privacy.description")}</Typography>
          <Anchor href="/privacy-policy" target="_blank">
            {t("privacy.link")}
          </Anchor>
        </Drawer>
      )}

      {!loading && !fieldShapeLoading ? (
        <Form
          form={form}
          name="field"
          onFinish={(v: FieldFormValues) => complete(v)}
          onFinishFailed={incomplete}
          style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}
        >
          {/* ── Header ──────────────────────────────────────────────────────── */}
          <div className="field-details-header">
            <div ref={nameContainerRef} className="field-details-name-container">
              {/* Read-only title — click to edit */}
              {!isEditingName && (
                <button type="button" className="field-details-name-display" onClick={() => setIsEditingName(true)}>
                  <Typography variant="h1" className="margin-0">
                    {watchedName || field?.name || t("fieldDetails.fieldNamePlaceholder")}
                  </Typography>
                  <Icon iconName="klim/edit-element" size="s" />
                </button>
              )}

              {/* Always mounted so the form value is preserved; hidden when not editing */}
              <div style={{ display: isEditingName ? "block" : "none" }}>
                <Input
                  dataTestId="field-details-name"
                  label=""
                  name="name"
                  initialValue={field?.name}
                  className="field-details-name-input"
                  placeholder={t("fieldDetails.fieldNamePlaceholder")}
                  rules={[{ required: true, message: t("fieldDetails.errors.name") }]}
                  validateTrigger="onChange"
                  onBlur={() => {
                    if (form.getFieldValue("name")) {
                      setIsEditingName(false);
                    } else {
                      form.validateFields(["name"]).catch(() => {});
                    }
                  }}
                />
              </div>
            </div>

            {/* Always mounted so isInRedzone is registered on the form as soon as the redzone loads, keeping the header meta accurate before the Settings tab is ever opened */}
            {redzone && (
              <Form.Item name="isInRedzone" hidden initialValue={fieldId ? field?.isInRedzone : redzone.accurate}>
                <div />
              </Form.Item>
            )}

            {metaParts.length > 0 && (
              <Typography variant="paragraph-s" color="grey-600" className="field-details-meta">
                {metaParts.join(" · ")}
              </Typography>
            )}

            {fieldId && (
              <Tabs
                size="small"
                className="field-details-tabs"
                activeKey={activeTab}
                onChange={(key) => setActiveTab(key as "overview" | "settings")}
                items={[
                  {
                    key: "overview",
                    label: (
                      <TabLabel showDot={cropsWithMissingTags.length > 0} dotColor="red">
                        {t("fieldDetails.overview")}
                      </TabLabel>
                    ),
                  },
                  { key: "settings", label: t("fieldDetails.editFieldDetails") },
                ]}
              />
            )}
          </div>

          {/* ── Scrollable content ─────────────────────────────────────────── */}
          <div className="field-details-content">
            {activeTab === "overview" && (
              <>
                {/* Editing request banners */}
                {isSubmissionFeatureEnabled && (
                  <div className="field-details-editing-requests">
                    {[EDITING_REQUEST_TYPE.FIELD_SHAPE, EDITING_REQUEST_TYPE.FIELD_SPLIT, EDITING_REQUEST_TYPE.FIELD_REDZONE].map((type) => (
                      <EditingRequestBanner key={type} type={type} farmId={farm?.id || ""} id={fieldId || ""} />
                    ))}
                  </div>
                )}

                {/* Map */}
                <div className="field-details-map-wrapper">
                  {/* Map description - only shown when no shape exists yet */}
                  {!fieldShape && <Typography variant="paragraph-m">{t("fieldDetails.setSizeExplainer")}</Typography>}

                  <div className="field-details-map">
                    {fieldShape && fieldShape.center ? (
                      hasMapCookieConsent ? (
                        <>
                          <MapStatic field={field} geometry={fieldShape} location={field?.location} />
                          <div className="field-details-map-actions">
                            <button type="button" className="field-details-action-btn" onClick={handleMapOpen}>
                              <Icon iconName="klim/edit-element" size="s" />
                              {t("fieldDetails.editShape")}
                            </button>
                            {showFieldSplitButton && fieldId && (
                              <button type="button" className="field-details-action-btn" onClick={() => setShowFieldSplitDrawer(true)}>
                                <Icon iconName="klim/field-split" size="s" />
                                {t("fieldDetails.splitField")}
                              </button>
                            )}
                          </div>
                        </>
                      ) : (
                        <div className="field-details-map-locked">
                          <div className="field-details-map-blurred-preview">
                            <LazyLoadImage alt="" src="/images/misc/map-placeholder.png" width="100%" height="100%" />
                          </div>
                          <Flex align="center" justify="center" className="field-details-map-locked-card">
                            <EmptyStateCard
                              iconName="tabler/cookie"
                              title={t("fieldDetails.mapLocked.title")}
                              description={t("fieldDetails.mapLocked.description")}
                              buttonText={t("fieldDetails.mapLocked.buttonText")}
                              onButtonClick={openCookiePreferences}
                              dataTestId="field-details-map-locked"
                            />
                          </Flex>
                        </div>
                      )
                    ) : (
                      <div className={`field-details-map-placeholder${locationError ? " error" : ""}`} onClick={handleMapOpen}>
                        <Icon color={locationError ? "#db3804" : "#065c45"} iconName="klim/plus" size="xl" />
                        <Typography variant="paragraph-m">{t("fieldDetails.setSizeMessage")}</Typography>
                      </div>
                    )}
                  </div>
                  {locationError && <div className="field-details-error-message">{t("fieldDetails.shapeMandatoryDescription")}</div>}
                </div>

                {/* Crops missing data */}
                {allMissingDataYears.length > 0 && fieldListField && (
                  <div className="field-details-missing-crops-card">
                    <div className="field-details-missing-crops-header">
                      <Typography variant="paragraph-m" bold className="margin-0">
                        {t("fieldDetails.missingCrops.title")}
                      </Typography>
                      <CounterBadge count={cropsWithMissingTags.length + yearsWithoutMainCrop.length} />
                    </div>
                    <Typography variant="paragraph-s" color="grey-600" className="field-details-missing-crops-subtitle">
                      {t("fieldDetails.missingCrops.subtitle")}
                    </Typography>
                    <Tabs
                      size="small"
                      className="field-details-missing-crops-tabs"
                      activeKey={activeMissingDataYear?.toString()}
                      onChange={(key) => setSelectedMissingDataYear(parseInt(key))}
                      items={allMissingDataYears.map((year) => ({
                        key: year.toString(),
                        label: (
                          <TabLabel showDot dotColor="red">
                            {year.toString()}
                          </TabLabel>
                        ),
                        children: <div className="field-details-missing-crops-tab-content">{renderMissingCropsYearContent(year)}</div>,
                      }))}
                    />
                  </div>
                )}

                {/* Carbon program participation card — only show for existing fields with Soil+3 enabled, user enrollment or an inventory program */}
                {fieldId && showCarbonProgramCard && (
                  <div className="field-details-carbon-card">
                    <div className="field-details-carbon-top">
                      <div className="field-details-carbon-header">
                        <Typography variant="paragraph-m" bold className="margin-0">
                          {t("fieldDetails.carbonProgram.title")}
                        </Typography>
                        <Flex align="center" gap="0.375rem">
                          <FieldParticipationTags
                            className="chip-pill capitalize"
                            availablePracticeBundles={fieldAvailablePracticeBundles}
                            carbonProgram={currentFarm?.carbonProgram}
                            activeFarmReportingYear={activeFarmReportingYear}
                          />
                        </Flex>
                      </div>
                      <Typography variant="paragraph-s" className="margin-0">
                        {isEnrolled ? t("fieldDetails.carbonProgram.enrolledDescription") : t("fieldDetails.carbonProgram.notEnrolledDescription")}
                      </Typography>
                    </div>

                    {(showOutcomeBasedPayout || showPracticeBasedPayout) && (
                      <>
                        <Divider />

                        <div className="field-details-carbon-payout">
                          {isEnrolled && (
                            <div className="field-details-carbon-payout-header">
                              <Typography variant="paragraph-s" bold className="margin-0">
                                {t("fieldDetails.carbonProgram.expectedPayout")}
                              </Typography>
                              <Tooltip title={expectedPayoutTooltip}>
                                <Icon iconName="tabler/info-circle" size="s" className="field-details-info-icon" />
                              </Tooltip>
                            </div>
                          )}
                          {showOutcomeBasedPayout && (
                            <>
                              {isInventoryProgram && (
                                <Typography variant="paragraph-s" color="grey-600" className="margin-0">
                                  {t("fieldTimeline:impactPanel.inventory.outcomeBasedPayout")}
                                </Typography>
                              )}
                              <div className="field-details-carbon-payout-amount">
                                <PayoutAmount
                                  amount={inventoryPayoutRange?.min ?? payoutDisplayValue}
                                  maxAmount={inventoryPayoutRange?.max}
                                  hasAllotment={showPayout}
                                  currency={currentFarm?.carbonProgram?.currency}
                                  co2Value={fieldId ? formatCo2DisplayValue(fieldCo2 ?? 0) : undefined}
                                  dataTestId="field-details-payout-amount"
                                />
                                {showPayout ? (
                                  <Tag
                                    colour="grey"
                                    text={
                                      inventoryPayoutRange
                                        ? formatPerHaRange(
                                            inventoryPayoutRange.min,
                                            inventoryPayoutRange.max,
                                            displayedFieldSize,
                                            currentFarm?.carbonProgram?.currency
                                          )
                                        : formatPerHaValue(payoutDisplayValue, displayedFieldSize, currentFarm?.carbonProgram?.currency)
                                    }
                                    className="field-details-no-payout-badge"
                                  />
                                ) : (
                                  <Tag
                                    colour="grey"
                                    text={
                                      isMissingPayoutData ? (
                                        <span className="field-details-missing-data-badge-content">
                                          <span className="field-details-tab-dot" />
                                          {t("fieldDetails.carbonProgram.missingData")}
                                        </span>
                                      ) : (
                                        t("fieldDetails.carbonProgram.noPayout")
                                      )
                                    }
                                    className="field-details-no-payout-badge"
                                  />
                                )}
                              </div>
                              {isMissingPayoutData && (
                                <Typography variant="paragraph-s" color="grey-600" className="margin-0">
                                  {t("fieldDetails.carbonProgram.missingDataDescription")}
                                </Typography>
                              )}
                            </>
                          )}
                          {showPracticeBasedPayout && (
                            <>
                              <Typography variant="paragraph-s" color="grey-600" className="margin-0">
                                {t("fieldTimeline:impactPanel.inventory.payoutsPractices.title")}
                              </Typography>
                              <div className="field-details-carbon-payout-amount">
                                <PayoutAmount
                                  amount={practiceBasedPayout}
                                  hasAllotment={hasPracticeBasedPayout}
                                  currency={currentFarm?.carbonProgram?.currency}
                                  dataTestId="field-details-practice-based-payout-amount"
                                />
                                {hasPracticeBasedPayout && (
                                  <Tag
                                    colour="grey"
                                    text={formatPerHaValue(practiceBasedPayout, displayedFieldSize, currentFarm?.carbonProgram?.currency)}
                                    className="field-details-no-payout-badge"
                                  />
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      </>
                    )}

                    {showEnrollmentActions && (
                      <>
                        <Divider />

                        <div className={`field-details-carbon-actions${isEnrolled ? " field-details-carbon-actions--single" : ""}`}>
                          {!isEnrolled && (
                            <Button type="secondary" block onClick={() => setShowLearnMoreDrawer(true)}>
                              {t("fieldDetails.carbonProgram.learnMore")}
                            </Button>
                          )}
                          <Tooltip title={enrollmentDisabledReason}>
                            <span style={{ display: "block", width: "100%" }}>
                              {isEnrolled ? (
                                <Button
                                  type="link"
                                  className="field-details-carbon-unenroll-btn"
                                  disabled={isEnrollmentActionDisabled || enrollmentLoading}
                                  loading={enrollmentLoading}
                                  onClick={() => handleEnrollField(false)}
                                >
                                  {t("fieldDetails.carbonProgram.unenrollField")}
                                </Button>
                              ) : (
                                <Button
                                  type="primary"
                                  block
                                  className="field-details-carbon-enroll-btn"
                                  disabled={isEnrollmentActionDisabled || enrollmentLoading}
                                  loading={enrollmentLoading}
                                  onClick={() => handleEnrollField(true)}
                                >
                                  {t("fieldDetails.carbonProgram.enrollField")}
                                </Button>
                              )}
                            </span>
                          </Tooltip>
                        </div>
                      </>
                    )}
                  </div>
                )}
                <ExtraInfo setVisible={setShowLearnMoreDrawer} type="enroll-fields" visible={showLearnMoreDrawer} />

                {/* SoilSight promo card — only for existing fields when FF is active */}
                {fieldId && isSoilSightIntegrationActive && (
                  <Flex vertical gap="0.75rem" className="field-details-soilsight-card">
                    <Tag colour="yellow" text={t("soilSightPromoCard.badge")} className="field-details-soilsight-badge" />
                    <Typography variant="paragraph-l" bold className="margin-0">
                      {t("soilSightPromoCard.title")}
                    </Typography>
                    <ul className="field-details-soilsight-benefits">
                      <li className="field-details-soilsight-benefit">
                        <Icon iconName="tabler/circle-check" size="s" />
                        <Typography variant="paragraph-s">{t("soilSightPromoCard.bullet1")}</Typography>
                      </li>
                      <li className="field-details-soilsight-benefit">
                        <Icon iconName="tabler/circle-check" size="s" />
                        <Typography variant="paragraph-s">{t("soilSightPromoCard.bullet2")}</Typography>
                      </li>
                      <li className="field-details-soilsight-benefit">
                        <Icon iconName="tabler/circle-check" size="s" />
                        <Typography variant="paragraph-s">{t("soilSightPromoCard.bullet3")}</Typography>
                      </li>
                    </ul>
                    <Button
                      type="primary"
                      size="small"
                      block
                      onClick={handleSoilSightCTAClick}
                      disabled={!canSubmitSoilSightImport}
                      loading={isSoilSightImportSubmitting}
                    >
                      {t("soilSightPromoCard.cta")}
                    </Button>
                  </Flex>
                )}

                {/* Privacy link */}
                <button type="button" className="field-details-privacy" onClick={handlePrivacyDrawer}>
                  <Typography variant="paragraph-m" color="green-400" className="margin-0">
                    {t("fieldDetails.privacyLink")}
                  </Typography>
                  <Icon iconName="tabler/info-circle" size="xs" />
                </button>
              </>
            )}

            {/* ── Settings tab ───────────────────────────────────────────────── */}
            {activeTab === "settings" && fieldId && (
              <div className="edit-field-wrapper">
                <Typography variant="h3" className="edit-field-section-title">
                  {t("fieldDetails.editSectionTitle") || "Field details"}
                </Typography>
                <Input
                  name="name"
                  label={`${t("fieldDetails.name")}`}
                  initialValue={watchedName || field?.name}
                  placeholder={t("fieldDetails.fieldNamePlaceholder")}
                  rules={[{ required: true, message: t("fieldDetails.errors.name") }]}
                  validateTrigger="onChange"
                />
                <Input
                  name="number"
                  label={t("fieldDetails.fieldNumberLabel")}
                  initialValue={field?.number}
                  placeholder={t("fieldDetails.fieldNumberPlaceholder")}
                />
                {field?.flik && (
                  <Input name="flik" label={t("fieldDetails.flikNumber")} initialValue={field.flik} disabled className="field-details-readonly-input" />
                )}
                {redzone && (
                  <div className="field-details-redzone">
                    <Typography variant="paragraph-m" className="margin-0">
                      {t("fieldDetails.isInRedzone")}
                    </Typography>
                    <Form.Item name="isInRedzone">
                      <KlimRadioGroup
                        options={[
                          { label: t("yes"), value: true },
                          { label: t("no"), value: false },
                        ]}
                        optionType="button"
                        buttonStyle="solid"
                        type="tertiary"
                        size="large"
                        fullWidth
                      />
                    </Form.Item>
                  </div>
                )}
                <div className="field-details-delete-card">
                  <Typography variant="paragraph-l" className="margin-0">
                    {t("fieldDetails.deleteField")}
                  </Typography>
                  <Typography variant="paragraph-s" className="margin-0">
                    {t("fieldDetails.deleteText")}
                  </Typography>
                  <span className="field-details-delete-btn-wrap">
                    <Tooltip title={canDelete ? undefined : t("fieldDetails.deleteFieldWithPracticesMessage")}>
                      <div
                        role="button"
                        onClick={!canDelete || loading || saving ? undefined : handleDeleteConfirmation}
                        className={`field-details-delete-btn${!canDelete || loading || saving ? " field-details-delete-btn--disabled" : ""}`}
                      >
                        <Icon iconName="tabler/trash" size="s" />
                        {t("fieldDetails.deleteField")}
                      </div>
                    </Tooltip>
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* ── Crop edit drawer ──────────────────────────────────────────── */}
          {fieldListField && editingCrop && showCropEditDrawer && (
            <AddEditCropDrawer
              field={fieldListField}
              existingCrop={editingCrop}
              allCrops={fieldCrops}
              availablePracticeBundles={fieldAvailablePracticeBundles}
              existingFertilizers={
                fieldPracticeData?.practices?.filter(
                  (p) => (p.type === PRACTICE_SLUG.MINERAL_FERTILIZERS || p.type === PRACTICE_SLUG.ORGANIC_FERTILIZERS) && p.cropId === editingCrop.id
                ) ?? []
              }
              existingTillages={fieldPracticeData?.practices?.filter((p) => p.type === PRACTICE_SLUG.TILLAGES && p.cropId === editingCrop.id) ?? []}
              year={editingCrop.year ?? moment().year()}
              showDrawer={showCropEditDrawer}
              setShowDrawer={setShowCropEditDrawer}
              fertilizerManagements={fieldPracticeData?.fertilizerManagements ?? []}
              carbonProgram={currentFarm?.carbonProgram}
              farmId={currentFarm?.id}
              formWithOnLoadValidation={!!editingCrop.id}
            />
          )}

          {/* ── Map editing drawer ─────────────────────────────────────────── */}
          <FieldLocation
            farm={farm}
            field={field}
            fieldSize={fieldSize}
            parentFieldShape={fieldShape}
            parentLocation={location}
            onClose={() => setShowMapDrawer(false)}
            setFieldSize={setFieldSize}
            setParentFieldShape={setFieldShape}
            setParentLocation={(v: Coordinates | null) => setCurrentLocation(v)}
            showMapDrawer={showMapDrawer}
            showLocationMessage={showLocationMessage}
          />

          {showFieldSplitDrawer && fieldShape?.geometry && field && (
            <SplitFieldDrawer field={field} geometry={fieldShape} onClose={() => setShowFieldSplitDrawer(false)} visible={showFieldSplitDrawer} />
          )}

          {/* ── Footer ────────────────────────────────────────────────────── */}
          <div className="field-details-footer">
            <Button
              type="secondary"
              block
              onClick={() => {
                form.resetFields();
                setShowFieldEditDrawer?.(false);
                onFinish?.();
              }}
            >
              {t("fieldDetails.cancel")}
            </Button>
            <Button type="primary" htmlType="submit" block disabled={loading || saving} loading={loading || saving}>
              {t("fieldDetails.save")}
            </Button>
          </div>
        </Form>
      ) : (
        <PageSpin />
      )}
    </div>
  ) : null;
};
